import React, { useEffect, useMemo, useState } from 'react';
import { SelectComponent } from 'components/SelectComponent';
import InfoSvg from 'assets/icons/ic-info.svg';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
  getFarmerKpi,
  setFarmerKpiChosenCategory,
  setFarmersKpiChosenAssessment,
  setFarmerChosenEmissionsAssessment,
  setFarmerKpiChosenEmissionsCategory, setFarmerFilterSpecies, setFarmerFilterProject,
} from 'store/farmerKpi/actions';
import { IAppState } from 'store';
import TableLoader from '../../../TableLoader';
import {
  EMISSIONS_BY_SOURCE,
  EMISSIONS_COLORS,
  labelsForGraph,
  getPerformanceIndicatorByValue,
  PERFORMANCE_INDICATORS, getPerformanceIndicatorByValueNew,
} from './config';
import { GraphDataModel } from './types';
import { useTranslation } from "react-i18next";

const FarmerKpiGeneralTab = () => {
  const [currentSpecies, setCurrentSpecies] = useState('');
  const { t, i18n } = useTranslation();

  const [currentAssessment, setCurrentAssessment] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getFarmerKpi());
  }, [dispatch]);

  const {
    isLoading,
    dataList,
  } = useSelector((state: IAppState) => state.farmerKpi);

  const assessmentOptions = useMemo(() => {
    let options = [];
    for (let key in dataList.Assessments) {
      options.push({
                value: key,
                label: `${dataList.Assessments[key].Name} ${new Date(dataList.Assessments[key].AssessmentDate)
                  .toString()
                 .substring(4, 15)}`,
              });
    }
    return options;
  }, [dataList]);


  let perfomanceAssesment = dataList?.KpiDataModels?.[1]?.AssessmentParts?.[0];
  let emissionAssessment = dataList?.KpiDataModels?.[0]?.AssessmentParts?.[0];
  if (dataList?.KpiDataModels?.[0]?.AssessmentParts[0]?.Values?.length === 1) {
    perfomanceAssesment = dataList?.KpiDataModels?.[0]?.AssessmentParts?.[0];
    emissionAssessment = null;
  }


  const metaData = perfomanceAssesment?.MetaData ? JSON.parse(perfomanceAssesment?.MetaData): null;
  const postFix = perfomanceAssesment?.UnitPostFix;
  const isMetaData = Boolean(metaData?.Good);



  const dataToDraw = useMemo(() => {
    let dataToShow: (GraphDataModel | { name: string; value: unknown })[] = [];
    if (perfomanceAssesment) {
      for (const [key, value] of Object.entries(perfomanceAssesment?.Values[0])) {
        if (labelsForGraph[key]) {
          dataToShow = [
            ...dataToShow,
            {
              name: `${labelsForGraph[key]}`,
              value: value,
            },
          ];
        }
      }
      dataToShow = [
        ...dataToShow,
        {
          name: `${perfomanceAssesment.Name}`,
          value: perfomanceAssesment?.Values[0].Value,
        },
      ];
    }

    return dataToShow;
  }, [perfomanceAssesment]);

  const emissionsGraphData = useMemo(() => {
    let data: Array<GraphDataModel> = [];
    if (emissionAssessment) {
      emissionAssessment.Values.forEach(v => {
        data.push({
          name: v.Name.slice(11),
          value:
            v.Value < 0
              ? Number((-1 * v.Value)?.toFixed(2))
              : Number(v.Value?.toFixed(2)),
        });
      });
    }
    return data;
  }, [emissionAssessment]);

  const onCategoryChange = (value: any) => {
    setCurrentSpecies(value.value);
    dispatch(getFarmerKpi({SpeciesFilterValues: [value.value]}));
    dispatch(setFarmerFilterSpecies(value.value))
  };

  const onAssessmentChoose = (value: any) => {
    const searchData = {
      SpeciesFilterValues: [currentSpecies],
      Assessments: [+value.value]
    }
    setCurrentAssessment(value.value);
    dispatch(getFarmerKpi(searchData));
    dispatch(setFarmerFilterProject(value.value))
  };

  const onEmissionsCategoryChange = (c: string) => {
    dispatch(setFarmerKpiChosenEmissionsCategory(c));
  };

  const onEmissionsAssessmentChange = (ass: string) => {
    dispatch(setFarmerChosenEmissionsAssessment(ass));
  };

  return (
    <div className="tab-pane">
      {isLoading ? (
        <TableLoader />
      ) : (
        <>
          {!perfomanceAssesment && !emissionAssessment && <div className="chart-wrapper general-chart">
            <h2 className="chart-heading heading-large">
              {t('RM_carbon')}
            </h2>
            <div className="kpi-row">
              <div className="kpi-item">
                <div className="form-group">
                  <SelectComponent
                    isMulti={false}
                    options={dataList?.SpeciesFilterValues?.map(c => ({ value: c, label: t(c) }))}
                    label={t('RM_spec')}
                    onChange={onCategoryChange}
                    value={currentSpecies}
                  />
                </div>
              </div>
              <div className="kpi-item">
                <div className="form-group">
                  <SelectComponent
                    isMulti={false}
                    options={assessmentOptions}
                    label={t('RM_ass')}
                    onChange={onAssessmentChoose}
                    value={currentAssessment}
                  />
                </div>
              </div>
            </div>
          </div>}
          {perfomanceAssesment && <div className="chart-wrapper general-chart">
            <h2 className="chart-heading heading-large">
              {t('RM_carbon')}
            </h2>
            <div className="kpi-row">
              <div className="kpi-item">
                <div className="form-group">
                  <SelectComponent
                    isMulti={false}
                    options={dataList?.SpeciesFilterValues?.map(c => ({ value: c, label: c }))}
                    label={t('RM_spec')}
                    onChange={onCategoryChange}
                    value={currentSpecies}
                  />
                </div>
              </div>
              <div className="kpi-item">
                <div className="form-group">
                  <SelectComponent
                    isMulti={false}
                    options={assessmentOptions}
                    label={t('RM_ass')}
                    onChange={onAssessmentChoose}
                    value={currentAssessment}
                  />
                </div>
              </div>
            </div>
            <div className="kpi-row">
              <div className="kpi-item">
                <div className="comparison-item performance-item">
                  <span
                    className="comparison-data"
                    style={{
                      color:
                        isMetaData ?
                          getPerformanceIndicatorByValueNew(
                            perfomanceAssesment.Values[0].Value, metaData?.Good, metaData?.Bad
                          ).color: 'black',
                    }}
                  >
                    {isMetaData ?
                      t(getPerformanceIndicatorByValueNew(
                        perfomanceAssesment.Values[0].Value, metaData?.Good, metaData?.Bad
                      ).word) : '—'}
                  </span>
                  <span className="comparison-subtitle">{t('RM_y_perf')}</span>
                </div>
              </div>
              <div className="kpi-item">
                <div className="comparison-item">
                  <span className="comparison-title">{t('RM_cur')}</span>
                  <span className="comparison-data">
                    {perfomanceAssesment &&
                    `${perfomanceAssesment?.Values[0].Value?.toFixed(2)}`}
                  </span>
                  <span className="comparison-subtitle">
                    {perfomanceAssesment?.UnitPostFix}
                    {/*<OverlayTrigger*/}
                    {/*  placement="right"*/}
                    {/*  overlay={*/}
                    {/*    <Popover id="Current Assessment">*/}
                    {/*      <Popover.Title as="span" className="tooltip-heading">*/}
                    {/*        What does g CO2/kg FPCM mean?*/}
                    {/*      </Popover.Title>*/}
                    {/*      <Popover.Content className="tooltip-text">*/}
                    {/*        Grams of carbon dioxide equivalent per kg of fat and*/}
                    {/*        protein corrected milk produced. The sum of all*/}
                    {/*        emissions generated on farm in 365 days, divided by*/}
                    {/*        the total volume of fat and protein corrected milk*/}
                    {/*        produced.*/}
                    {/*      </Popover.Content>*/}
                    {/*    </Popover>*/}
                    {/*  }*/}
                    {/*>*/}
                    {/*  <img*/}
                    {/*    src={InfoSvg}*/}
                    {/*    alt="info img"*/}
                    {/*    className="tooltip-trigger"*/}
                    {/*  />*/}
                    {/*</OverlayTrigger>*/}
                  </span>
                </div>
              </div>
              <div className="kpi-item">
                <div className="comparison-item">
                  <span className="comparison-title">{t('RM_al_ava')}</span>
                  <span className="comparison-data">
                    {perfomanceAssesment && perfomanceAssesment?.Values[0].AlltechAverage ?
                    `${perfomanceAssesment?.Values[0].AlltechAverage?.toFixed(2)}` : '—'}
                    {perfomanceAssesment && perfomanceAssesment?.Values[0].AlltechAverage ? (
                      <span
                        className="inner-data"
                        style={{
                          color:
                            perfomanceAssesment?.Values[0]?.Value >
                            perfomanceAssesment?.Values[0]?.AlltechAverage
                              ? '#ff333b'
                              : '#37a041',
                          borderColor:
                            perfomanceAssesment?.Values[0]?.Value >
                            perfomanceAssesment?.Values[0]?.AlltechAverage
                              ? '#ff333b'
                              : '#37a041',
                        }}
                      >
                        {perfomanceAssesment &&
                        perfomanceAssesment?.Values[0]?.Value >
                        perfomanceAssesment?.Values[0]?.AlltechAverage
                          ? `+${
                            perfomanceAssesment?.Values[0].Value -
                            perfomanceAssesment?.Values[0].AlltechAverage
                          }`
                          : `-${
                            perfomanceAssesment?.Values[0].AlltechAverage -
                            perfomanceAssesment?.Values[0].Value
                          }`}
                      </span>
                    ):null}
                  </span>
                  <span className="comparison-subtitle">
                    {perfomanceAssesment?.UnitPostFix}
                    {/*<OverlayTrigger*/}
                    {/*  placement="right"*/}
                    {/*  overlay={*/}
                    {/*    <Popover id="Alltech Average">*/}
                    {/*      <Popover.Title as="span" className="tooltip-heading">*/}
                    {/*        What does g CO2/kg FPCM mean?*/}
                    {/*      </Popover.Title>*/}
                    {/*      <Popover.Content className="tooltip-text">*/}
                    {/*        Grams of carbon dioxide equivalent per kg of fat and*/}
                    {/*        protein corrected milk produced. The sum of all*/}
                    {/*        emissions generated on farm in 365 days, divided by*/}
                    {/*        the total volume of fat and protein corrected milk*/}
                    {/*        produced.*/}
                    {/*      </Popover.Content>*/}
                    {/*    </Popover>*/}
                    {/*  }*/}
                    {/*>*/}
                    {/*  <img*/}
                    {/*    src={InfoSvg}*/}
                    {/*    alt="info img"*/}
                    {/*    className="tooltip-trigger"*/}
                    {/*  />*/}
                    {/*</OverlayTrigger>*/}
                  </span>
                </div>
              </div>
              <div className="kpi-item">
                <div className="comparison-item">
                  <span className="comparison-title">{t('RM_10')}</span>
                  <span className="comparison-data">
                    {perfomanceAssesment && perfomanceAssesment?.Values[0].TopTenPercent ?
                    `${perfomanceAssesment?.Values[0].TopTenPercent?.toFixed(2)}`: '—'}
                  </span>
                  <span className="comparison-subtitle">
                    {perfomanceAssesment?.UnitPostFix}
                    {/*<OverlayTrigger*/}
                    {/*  placement="right"*/}
                    {/*  overlay={*/}
                    {/*    <Popover id="Alltech Top 10%">*/}
                    {/*      <Popover.Title as="span" className="tooltip-heading">*/}
                    {/*        What does g CO2/kg FPCM mean?*/}
                    {/*      </Popover.Title>*/}
                    {/*      <Popover.Content className="tooltip-text">*/}
                    {/*        Grams of carbon dioxide equivalent per kg of fat and*/}
                    {/*        protein corrected milk produced. The sum of all*/}
                    {/*        emissions generated on farm in 365 days, divided by*/}
                    {/*        the total volume of fat and protein corrected milk*/}
                    {/*        produced.*/}
                    {/*      </Popover.Content>*/}
                    {/*    </Popover>*/}
                    {/*  }*/}
                    {/*>*/}
                    {/*  <img*/}
                    {/*    src={InfoSvg}*/}
                    {/*    alt="info img"*/}
                    {/*    className="tooltip-trigger"*/}
                    {/*  />*/}
                    {/*</OverlayTrigger>*/}
                  </span>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={dataToDraw.length * 57}>
              <BarChart
                layout="vertical"
                data={dataToDraw}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="500 1" horizontal={false} />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" scale="band" width={90} />
                <Tooltip />
                <Bar dataKey="value" barSize={30} radius={[0, 4, 4, 0]}>
                  {dataToDraw.map(d => (
                    <Cell
                      fill={isMetaData ? getPerformanceIndicatorByValueNew(d.value, metaData?.Good, metaData?.Bad).color :'#37a041'}
                      key={d.name}
                    />
                  ))}
                  <LabelList
                    dataKey="value"
                    position="right"
                    style={{ fill: '#000' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {isMetaData &&             <div className="chart-legend traffic-light-legend">
              <ul className="legend-list">
                <li className="list-item">
                  <span
                    style={{
                      backgroundColor: PERFORMANCE_INDICATORS.good.color,
                    }}
                  />
                  <span className="list-item-text">{`< ${metaData?.Good}`}</span>
                  {postFix}
                </li>
                <li className="list-item">
                  <span
                    style={{
                      backgroundColor:
                      PERFORMANCE_INDICATORS.satisfactory.color,
                    }}
                  />
                  <span className="list-item-text">{`${metaData?.Good} - ${metaData?.Bad}`}</span>
                  {postFix}
                </li>
                <li className="list-item">
                  <span
                    style={{
                      backgroundColor:
                      PERFORMANCE_INDICATORS.unsatisfactory.color,
                    }}
                  />
                  <span className="list-item-text">{`> ${metaData?.Bad}`}</span>
                  {postFix}
                </li>
                {/*<li className="list-item">*/}
                {/*  <span*/}
                {/*    style={{*/}
                {/*      backgroundColor:*/}
                {/*        "#5b6670",*/}
                {/*    }}*/}
                {/*  />*/}
                {/*  <span className="list-item-text">{'Additional averages'}</span>*/}
                {/*  gCO2e*/}
                {/*</li>*/}
              </ul>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Popover id="Performance indicator">
                    <Popover.Content className="tooltip-text">
                      {perfomanceAssesment?.InfoText}
                    </Popover.Content>
                  </Popover>
                }
              >
                <img src={InfoSvg} alt="info img" className="tooltip-trigger" />
              </OverlayTrigger>
            </div>}

          </div>}
          {emissionAssessment && <div className="chart-wrapper emissions-chart">
            <h2 className="chart-heading heading-large">
              Your farm emissions by source
            </h2>
            <div className="kpi-row kpi-row-sm">
              <div className="kpi-item">
                <div className="form-group">
                  <SelectComponent
                    isMulti={false}
                    options={dataList?.SpeciesFilterValues?.map(c => ({ value: c, label: c }))}
                    label={t('RM_spec')}
                    onChange={onCategoryChange}
                    value={currentSpecies}
                  />
                </div>
              </div>
              <div className="kpi-item">
                <div className="form-group">
                  <SelectComponent
                    isMulti={false}
                    options={assessmentOptions}
                    label={t('RM_ass')}
                    onChange={onAssessmentChoose}
                    value={currentAssessment}
                  />
                </div>
              </div>
            </div>
            {emissionsGraphData.length > 0 && (
              <BarChart
                width={1150}
                height={320}
                data={emissionsGraphData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" barSize={50} radius={[3, 3, 0, 0]}>
                  {emissionsGraphData.map((d, i) => (
                    <Cell
                      fill={EMISSIONS_COLORS[i % EMISSIONS_COLORS.length]}
                      key={d.name}
                    />
                  ))}
                  <LabelList
                    dataKey="value"
                    position="right"
                    style={{ fill: '#000' }}
                  />
                </Bar>
              </BarChart>
            )}

            <p className="chart-description">
              This bar chart demonstrates the percentage contribution towards
              total farm emissions from the dairy enterprise, by their source.
            </p>
            {emissionsGraphData.length > 0 && (
              <div className="chart-legend">
                <ul className="legend-list">
                  {emissionsGraphData.map((emission, index) => (
                    <li className="list-item" key={emission.name}>
                      <span
                        style={{
                          backgroundColor:
                            EMISSIONS_COLORS[index % EMISSIONS_COLORS.length],
                        }}
                      />
                      {emission.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>}
        </>
      )}
    </div>
  );
};

export { FarmerKpiGeneralTab };
